import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AppContext = createContext(null);

function dedupeTeam(list) {
  const byName = {};
  (list || []).forEach((t) => {
    const ex = byName[t.name];
    if (!ex || (t.status === "Active" && ex.status !== "Active")) byName[t.name] = t;
  });
  return Object.values(byName);
}

export const localISO = (x = new Date()) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;

function computeRange(preset) {
  const iso = localISO;
  const today = new Date();
  if (preset === "Today") return { from: iso(today), to: iso(today) };
  if (preset === "Yesterday") { const y = new Date(today); y.setDate(y.getDate() - 1); return { from: iso(y), to: iso(y) }; }
  if (preset === "This Week") { const s = new Date(today); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); return { from: iso(s), to: iso(today) }; }
  if (preset === "Last Week") { const s = new Date(today); s.setDate(s.getDate() - ((s.getDay() + 6) % 7) - 7); const e = new Date(s); e.setDate(s.getDate() + 6); return { from: iso(s), to: iso(e) }; }
  if (preset === "This Month") { const s = new Date(today.getFullYear(), today.getMonth(), 1); return { from: iso(s), to: iso(today) }; }
  if (preset === "Last Month") { const s = new Date(today.getFullYear(), today.getMonth() - 1, 1); const e = new Date(today.getFullYear(), today.getMonth(), 0); return { from: iso(s), to: iso(e) }; }
  return { from: null, to: null };
}

export const AppProvider = ({ children }) => {
  const [branch, setBranch] = useState("All");
  const [meta, setMeta] = useState({ branches: [], stages: [], touchpoints: [], followup_protocol: [], team: [] });
  const [team, setTeam] = useState([]);
  const [kpis, setKpis] = useState({});
  const [person, setPersonState] = useState(() => localStorage.getItem("ync_person") || "");
  const [notifications, setNotifications] = useState({ count: 0, items: [] });
  const [dateRange, setDateRange] = useState({ preset: "This Month", ...computeRange("This Month") });

  const setPerson = (p) => { setPersonState(p); localStorage.setItem("ync_person", p); };
  const setPreset = (preset) => setDateRange((r) => (preset === "Custom" ? { ...r, preset } : { preset, ...computeRange(preset) }));
  const setCustomRange = (from, to) => setDateRange({ preset: "Custom", from, to });

  const loadMeta = useCallback(async () => {
    try { const res = await api.get("/meta"); setMeta(res.data); setTeam(dedupeTeam(res.data.team)); } catch {}
  }, []);
  const loadTeam = useCallback(async () => {
    try { const res = await api.get("/member-kpis"); setTeam(dedupeTeam(res.data.team)); setKpis(res.data.kpis || {}); } catch {}
  }, []);
  const loadNotifications = useCallback(async () => {
    try { const res = await api.get("/notifications"); setNotifications(res.data); } catch {}
  }, []);

  useEffect(() => {
    loadMeta(); loadTeam(); loadNotifications();
    const t = setInterval(loadNotifications, 60000);
    return () => clearInterval(t);
  }, [loadMeta, loadTeam, loadNotifications]);

  const currentRole = team.find((t) => t.name === person)?.role || "";

  return (
    <AppContext.Provider value={{ branch, setBranch, meta, loadMeta, team, loadTeam, kpis, person, setPerson,
      currentRole, dateRange, setPreset, setCustomRange, notifications, loadNotifications }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => useContext(AppContext);
